import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { SiteNav } from "@/components/landing/SiteNav";
import { SiteFooter } from "@/components/landing/SiteFooter";

const plans = [
  {
    name: "Founder",
    price: "₹14,999",
    period: "/month",
    description: "For individual founders tracking their startup",
    features: [
      "Up to 9 KPIs",
      "1 company",
      "100 recipients (board + team)",
      "WhatsApp summaries",
      "Weekly PDF exports",
      "Email & SMS fallback",
    ],
    cta: "Start with Founder",
    highlighted: false,
  },
  {
    name: "Studio",
    price: "₹49,999",
    period: "/month",
    description: "For studios managing multiple companies",
    features: [
      "Up to 5 companies",
      "Portfolio digest",
      "Monthly LP pack",
      "Everything in Founder",
      "Priority support",
      "Custom integrations",
    ],
    cta: "Start with Studio",
    highlighted: true,
  },
  {
    name: "Fund",
    price: "Custom",
    period: "",
    description: "For funds with 10+ portfolio companies",
    features: [
      "10+ companies",
      "SSO authentication",
      "Data room exports",
      "Dedicated account manager",
      "Custom analytics",
      "API access",
    ],
    cta: "Contact sales",
    highlighted: false,
  },
];

const Pricing = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteNav />

      <section className="container mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-12 sm:pb-20 flex-1">
        <div className="text-center space-y-4 mb-12 sm:mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
            Simple, transparent pricing
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            Choose the plan that fits your stage. All plans include WhatsApp
            summaries, traffic light KPIs, and investor-ready exports.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl border p-6 sm:p-8 bg-card transition-all hover:shadow-lg ${
                plan.highlighted
                  ? "border-primary shadow-md md:scale-105"
                  : "border-border"
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium whitespace-nowrap">
                  Most popular
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {plan.description}
                  </p>
                </div>

                <div className="flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-bold tabular-nums">
                    {plan.price}
                  </span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>

                <Link to="/auth" className="block">
                  <Button
                    className="w-full"
                    variant={plan.highlighted ? "default" : "outline"}
                  >
                    {plan.cta}
                  </Button>
                </Link>

                <div className="pt-4 space-y-3">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 sm:mt-16 text-center space-y-4">
          <p className="text-muted-foreground">
            All plans include a 7-day grace period for failed payments. Cancel
            anytime.
          </p>
          <p className="text-sm text-muted-foreground">
            Prices in INR. USD pricing available for international customers.
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Pricing;
